import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Editor } from '@tiptap/react';
import { StreamingTranscriber } from 'assemblyai';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';

type UseAudioTranscriptionOptions = {
	onBuffer: (buffer: ArrayBuffer) => void;
	editor: Editor | null;
};

export enum AudioState {
	Idle,
	Starting,
	Recording,
	Stopping,
	Rephrasing,
	Complete,
}

export enum SourceMode {
	Unselected,
	Raw,
	Rephrased,
}

const mergeBuffers = (lhs: Int16Array, rhs: Int16Array) => {
	const mergedBuffer = new Int16Array(lhs.length + rhs.length);
	mergedBuffer.set(lhs, 0);
	mergedBuffer.set(rhs, lhs.length);
	return mergedBuffer;
};

// The sample_rate is the number of audio samples per second, measured in hertz (Hz).
// Minimum quality: 8_000 (8 kHz)
// Medium quality: 16_000 (16 kHz)
// Maximum quality: 48_000 (48 kHz)

const SAMPLE_RATE = 16_000;

const createMicrophone = () => {
	let ctx: AudioContext;
	let stream: MediaStream;
	let source;
	let audioBufferQueue = new Int16Array(0);

	return {
		async requestPermission() {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		},

		async start({
			onBuffer,
			onSample,
		}: {
			// The buffer data is the raw, unprocessed data that will be used to draw the visualizer
			onBuffer: (buffer: ArrayBuffer) => void;
			// The sample data is chunked, normalized, and sent to the server for transcription
			onSample: (buffer: Uint8Array) => void;
		}) {
			if (!stream) {
				stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			}

			ctx = new AudioContext({
				sampleRate: SAMPLE_RATE,
				latencyHint: 'balanced',
			});

			source = ctx.createMediaStreamSource(stream);

			await ctx.audioWorklet.addModule(`/worklets/float32-to-int16-audio.js?v=${import.meta.env.REACT_APP_GIT_SHA}`);

			const audioWorkletNode = new AudioWorkletNode(ctx, 'float32-to-int16-audio');

			source.connect(audioWorkletNode).connect(ctx.destination);

			audioWorkletNode.port.onmessage = (event) => {
				// We receive a buffer of 16-bit integers from the audio worklet and have to append it
				// to that saved in state since we receive the data in chunks.
				const currentBuffer = new Int16Array(event.data.audio_data);
				audioBufferQueue = mergeBuffers(audioBufferQueue, currentBuffer);

				onBuffer(event.data.audio_data);

				// Determine the number of milliseconds of audio data we have in the buffer
				const bufferDuration = (audioBufferQueue.length / ctx.sampleRate) * 1000;

				// Only send the buffer if it's at least 100ms long
				if (bufferDuration >= 100) {
					const totalSamples = Math.floor(ctx.sampleRate * 0.1);

					// Convert the buffer to a Uint8Array so that it can be sent to the server
					const finalBuffer = new Uint8Array(audioBufferQueue.subarray(0, totalSamples).buffer);

					audioBufferQueue = audioBufferQueue.subarray(totalSamples);

					onSample(finalBuffer);
				}
			};
		},
		stop() {
			stream?.getTracks().forEach((track) => track.stop());

			if (ctx.state !== 'closed') {
				ctx?.close();
			}

			audioBufferQueue = new Int16Array(0);
		},
	};
};

type StateRef = {
	audioState: AudioState;
	editor: Editor | null;
	mic: ReturnType<typeof createMicrophone> | null;
	mode: SourceMode;
	service: StreamingTranscriber | null;
	rawText: string;
	rephrasedText: string;
};

const defaultState: StateRef = {
	audioState: AudioState.Idle,
	editor: null,
	mic: null,
	mode: SourceMode.Unselected,
	service: null,
	rawText: '',
	rephrasedText: '',
};

export const useAudioTranscription = ({ onBuffer, editor }: UseAudioTranscriptionOptions) => {
	const queryClient = useQueryClient();
	const http = useHttpClient();
	const toast = useToast();
	const { t } = useTranslation('feedback');

	// Mutable state controlled by the user and which directly affects the UI
	const [audioState, setAudioState] = useState(AudioState.Idle);
	const [mode, setMode] = useState(SourceMode.Unselected);

	// Ref state needs to be tracked but does not need to trigger re-renders
	const stateRef = useRef(defaultState);
	const initializedRef = useRef(false);

	// A few of these values need to be accessed in functions we initialize into closures so we'll store
	// them in a ref. Otherwise we'd need to reinitialize the functions each time, which pretty much just
	// doesn't work. This saves the values every time state changes.
	Object.assign(stateRef.current, {
		audioState,
		editor,
		mode,
	});

	const startRecording = useCallback(async () => {
		stateRef.current.editor?.setEditable(false, false);

		try {
			setAudioState(AudioState.Starting);

			const mic = createMicrophone();
			await mic.requestPermission();

			const token = await queryClient.fetchQuery({
				queryKey: ['assemblyai-temp-token'],
				queryFn: async () => {
					const res = await http.post('/transcription/tokens');
					return res.data.token;
				},

				// The expiration time of the token is 5 minutes so we want to refresh it before it expires
				staleTime: 4.5 * 60 * 1000,
			});

			const service = new StreamingTranscriber({ token: token, sampleRate: SAMPLE_RATE });

			const textChunks: { [k: number]: string } = {};

			service.on('turn', (turn: { turn_order: number; transcript: string }) => {
				// The transcriptions returned from the service are chunked by time. We'll receive several messages in
				// a row with the same initial audio_start timestamp, but with updated text. Therefore, we need to
				// continuously update the textChunks object with the latest text for each timestamp and then concatenate
				// them all together in the correct order.
				textChunks[turn.turn_order] = turn.transcript;
				const text = Object.entries(textChunks)
					.sort(([a], [b]) => Number(a) - Number(b))
					.map(([, text]) => text)
					.join(' ');

				stateRef.current.editor?.commands.setContent(text);
				stateRef.current.rawText = text;
			});

			await service.connect();

			await mic.start({
				onSample: (data) => service.sendAudio(data.buffer),
				onBuffer,
			});

			setAudioState(AudioState.Recording);

			Object.assign(stateRef.current, {
				mic,
				service,
			});
		} catch (err) {
			// NOTE: The `false` in here is a not well-documented parameter that tells the editor not to emit an update event
			// when setEditable is fired. Honestly, it doesn't make sense to me that the default is `true`. Without this we'll
			// get into circulrar update -> reset -> update -> reset loops.
			stateRef.current.editor?.setEditable(true, false);

			// The error that is thrown if the user denies microphone access is an instance of NotAllowedError, but for
			// whatever reason it doesn't exist as far as TypeScript is concerned. We can simply convert the error to a string
			// and check the name to see if it matches as a workaround.
			if (err && typeof err === 'object' && err.toString().includes('NotAllowedError')) {
				toast({
					title: t('transcribe.access_denied'),
					status: 'error',
				});
			}

			setAudioState(AudioState.Idle);
		}
	}, [http, queryClient, onBuffer, t, toast]);

	const rephrase = useMutation({
		mutationFn: async (text: string) => {
			const res = await http.post('/constructive_feedback/text_processing/rephrase_feedback_message_text', { text });

			// If the user has reset state before the rephrasing is complete we don't want to update the editor
			if (stateRef.current.audioState < AudioState.Rephrasing) {
				return;
			}

			const rephrased = res.data.rephrased_text;
			stateRef.current.rephrasedText = rephrased;
			setAudioState(AudioState.Complete);

			// If mode is set to rephrased, we'll update the editor with the rephrased text
			if (stateRef.current.mode === SourceMode.Rephrased) {
				stateRef.current.editor?.commands.setContent(rephrased);
			}
		},
	});

	const stopRecording = useCallback(async () => {
		if (mode === SourceMode.Raw) {
			stateRef.current.editor?.setEditable(true, false);
		}

		setAudioState(AudioState.Stopping);

		stateRef.current.mic?.stop();
		await stateRef.current.service?.close();

		setAudioState(AudioState.Rephrasing);

		await rephrase.mutateAsync(stateRef.current.rawText);

		stateRef.current.editor?.setEditable(true, false);
	}, [mode, rephrase]);

	const reset = useCallback(() => {
		stateRef.current.editor?.setEditable(true, false);

		// Reset is called when the modal closes. We need to stop the microphone and close the service if they're still
		// running to prevent memory leaks.)
		stateRef.current.mic?.stop();
		stateRef.current.service?.close();

		// Reset to initial state
		setAudioState(AudioState.Idle);
		setMode(SourceMode.Unselected);

		Object.assign(stateRef.current, defaultState);
	}, []);

	if (editor && !initializedRef.current) {
		// When the user *types* in the editor we'll reset the state so they can re-record if they want to
		initializedRef.current = true;
		editor.on('update', reset);
	} else if (!editor) {
		initializedRef.current = false;
	}

	const sourceRaw = useCallback(() => {
		setMode(SourceMode.Raw);

		if (audioState > AudioState.Recording) {
			stateRef.current.editor?.setEditable(true, false);
		}

		if (audioState === AudioState.Complete) {
			stateRef.current.editor?.commands.setContent(stateRef.current.rawText);
		}
	}, [audioState]);

	const sourceRephrased = useCallback(() => {
		setMode(SourceMode.Rephrased);
		stateRef.current.editor?.setEditable(true, false);

		if (audioState === AudioState.Complete) {
			stateRef.current.editor?.commands.setContent(stateRef.current.rephrasedText);
		}
	}, [audioState]);

	// isActive - used to disable things like the submit button while the service is running
	// By default transcription is considered active if it's doing *anything*
	let isActive = audioState !== AudioState.Idle && audioState !== AudioState.Complete;

	// But, if you set it to use only the raw text we don't care about the post-recording state
	if (mode === SourceMode.Raw && audioState > AudioState.Recording) {
		isActive = false;
	}

	// However, the user still needs to make a selection before we can consider the transcription complete,
	// so even if the state is "complete" it's still considered active if the user hasn't made a selection
	if (AudioState.Complete && mode === SourceMode.Unselected) {
		isActive = false;
	}

	// This isn't a 1:1 mapping of the state to the UI, but it's the concept of recording "from the user's perspective".
	// If they elect to use the raw text and we're processing the audio for rephrasing then we consider it to not be recording.
	let isRecording = audioState >= AudioState.Recording && mode !== SourceMode.Raw;

	if (audioState === AudioState.Complete && mode === SourceMode.Rephrased) {
		isRecording = false;
	}

	return {
		isActive,
		mode,
		reset,
		isRecording,
		state: audioState,
		start: startRecording,
		stop: stopRecording,
		sourceRaw,
		sourceRephrased,
	};
};

export type Transcribe = ReturnType<typeof useAudioTranscription>;
